import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes.js';
import { type PortableTextBlock } from 'sanity';

export interface BagNotFoundCopy {
  bagNotFoundTitle: string;
  bagNotFoundPreTitle: string;
}

export interface Coordinates {
  long: number;
  lat: number;
}

export interface Location {
  label: string;
  coordinates: Coordinates;
}

export interface SuggestionsApiResponse {
  response: {
    success: boolean;
    payload: Origin[];
  };
}

export interface BagsApiResponse {
  success: boolean;
  payload: {
    items: SurpriseBag[];
  };
  canLoadMore: boolean;
}
export interface OriginCoordinates {
  latitude: number;
  longitude: number;
}
export interface Origin {
  displayName: string;
  boundingBox?: {
    bottomRight: OriginCoordinates;
    topLeft: OriginCoordinates;
  };
  center: OriginCoordinates;
  radius?: number;
}

export interface ItemCategoryDetails {
  id: string;
  name: string;
  iconUrlLight?: string;
  iconUrlDark?: string;
}

/**
 * Availability tag variants. The SEO API returns exactly one of these as the
 * first entry of `itemTags`, so they are mutually exclusive.
 */
export type ItemTagAvailabilityVariant =
  | 'X_ITEMS_LEFT'
  | 'SOLD_OUT'
  | 'NOTHING_TO_SAVE_TODAY'
  | 'CHECK_AGAIN_LATER';

/** Merchandising variants, appended after the availability tag. */
export type ItemTagMerchandisingVariant =
  | 'HIDDEN_GEM'
  | 'POPULAR'
  | 'SELLING_FAST'
  | 'RARE_FIND'
  | 'ENDING_SOON'
  | 'PLAN_AHEAD'
  | 'RESERVE_AHEAD'
  | 'VEGETARIAN'
  | 'VEGAN'
  | 'DINE_IN'
  | 'LOCAL_HERO'
  | 'NEW';

export type ItemTagVariant = ItemTagAvailabilityVariant | ItemTagMerchandisingVariant;

/**
 * A tag from the SEO API's `itemTags`. All copy is localized server-side, so it
 * is rendered as-is rather than run through i18n.
 */
export interface SurpriseBagItemTag {
  /** Groups related variants; every merchandising variant shares the `GENERIC` id. */
  id: string;
  /** The specific tag, and what drives its badge styling. */
  variant: ItemTagVariant | string;
  /** Badge-length copy, e.g. `2 left`. */
  shortText: string | null;
  longText: string | null;
  description: string | null;
  descriptionHeading: string | null;
}

export interface SurpriseBagItem {  
  coverPicture: {
    currentUrl: string,
    isAutomaticallyCreated: boolean,
    pictureId: string,
  },
  dietCategories: string[],
  itemCategory: string,
  itemCategoryV2?: string,
  itemSubCategory?: string,
  itemCategoryV2Details?: ItemCategoryDetails | null,
  itemSubCategoryDetails?: ItemCategoryDetails | null,
  itemId: string,
  itemPrice: {
    code: string,
    decimals: 2,
    minorUnits: number,
  },
  itemValue: {
    code: string,
    decimals: 2,
    minorUnits: number,
  },
  logoPicture: {
    currentUrl: string,
    isAutomaticallyCreated: boolean,
    pictureId: string,
  },
  name: string,
  averageOverallRating?: {
    averageOverallRating: number,
    averageCollectionExperienceRating: number,
    averageFoodQualityRating: number,
    averageContentsVarietyRating: number,
    averageFoodQuantityRating: number,
    ratingCount: number,
    monthCount: number,
  },
  description?: string,
}

export interface BagStore {
  branch: string,
  logoPicture: {
    currentUrl: string,
    isAutomaticallyCreated: boolean,
    pictureId: string,
  },
  storeId: string,
  storeName: string,
  storeLocation: {
    address: {
      addressLine: string,
      country: {
        isoCode: string,
        name: string,
      },
      city: string,
    },
    location: Location | OriginCoordinates,
    storeName: string,
  },
}
export interface SurpriseBag {
  displayName: string;
  distance: number;
  inSalesWindow: boolean;
  item: SurpriseBagItem;
  itemsAvailable: number,
  pickupInterval: {
    end: string,
    start: string,
  },
  pickupLocation: {
    address: {
      addressLine: string,
      country: {
        isoCode: string,
        name: string,
      },
    },
    location: OriginCoordinates,
  },
  purchaseEnd: string,
  soldOutAt?: string | null,
  store: BagStore,
  localizationCode: string,
  /** Ordered by the API: availability tag first, then merchandising tags. */
  itemTags?: SurpriseBagItemTag[] | null,
}

export interface BagMetadataTexts {
  metaTitle: string;
  metaDescription: string;
}

export interface SurpriseBagTranslations {
  descriptionTitle: string;
  reminderText: string;
  moreInfoTitle: string;
  moreInfoContent: string;
  ratingTitle: string;
  ratingSubtitle: string;
  ingredientsCtaLabel: string;
  firstRatingTitle: string;
  secondRatingTitle: string;
  thirdRatingTitle: string;
  fourthRatingTitle: string;
  nudge: {
    nudgeTitle: string;
    steps: Array<{
      icon: string;
      title: string;
      content: Array<PortableTextBlock>;
    }>;
  };
  mapSectionTitle: string;
  mapErrorLabel: string;
  qrTitle: string;
  qrSubtitle: string;
  ctaLabel: string;
  popUpTitle: string;
  popUpDescription: string;
  popUpCtaLabel: string;
  language: LOCALE_CODE;
  timingTexts: CollectionLabels;
  metadataTexts: BagMetadataTexts,
  mainBreadcrumbText: string;
  surpriseBagKeyword: string;
  nearbyBagsLabel?: string;
}

export const SURPRISE_BAG_NEARBY_BAGS_LIMIT = 3;
export const SURPRISE_BAG_NEARBY_BAGS_FETCH_SIZE = 10;

export const SURPRISE_BAG_RESULTS_PER_PAGE = 21;

export interface CollectionLabels {
  otherDayText: string;
}

export const BAG_AVAILABILITY = {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  SOLD_OUT: 'sold_out',
}

export const BAG_PLACEHOLDER_HERO_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMB/UmhV3cAAAAASUVORK5CYII=';

export interface Breadcrumb {
  name: string;
  path: string;
  isLast: boolean;
}